import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import ideaStatusGroupsQuery from '@aurora/shared-client/components/ideas/IdeaStatusGroups.query.graphql';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import type { Idea } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  ContextNodeFragment,
  IdeaStatusGroupsQuery,
  IdeaStatusGroupsQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useState } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import useTranslation from './../../../useTranslation';
import localStyles from './IdeaMessagesStatusFilter.module.pcss';
import type {
  IdeaStatusInformationQuery,
  IdeaStatusInformationQueryVariables
} from '../../../../types/graphql-types';
import ideaStatusPropertiesQuery from '../../../ideas/IdeaStatusInformation.query.graphql';
import IdeaMessagesStatusFilterDropdownItem from './IdeaMessagesStatusFilterDropdownItem';

export type StatusGroup = {
  /**
   * The group key
   */
  groupKey: string;
  /**
   * the group title
   */
  groupTitle: string;
};

export type StatusItem = {
  /**
   * The status key
   */
  statusKey: string;
  /**
   * The text to display for the status item
   */
  text: string;
};

interface Props {
  /**
   * The selected statuses, e.g. through the querystring
   */
  selectedStatuses: string[];
  /**
   * The node which filtering should be scoped to.
   */
  contextNode: ContextNodeFragment;
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * Callback function when a status filter is selected.
   *
   * @callback
   */
  onChange?: (items: StatusItem[]) => void;
  /**
   * The selected status group
   */
  statusGroup?: StatusGroup;
}

/**
 * The idea status filter for the ideas widget filter bar.
 * This will load a dropdown of statuses that contain one or more idea messages.
 *
 * @author rishabh.jivan
 */
const IdeaMessagesStatusFilterDropdown = ({
  selectedStatuses,
  className,
  contextNode,
  onChange,
  statusGroup = null
}: Props): React.ReactElement => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const i18n = useTranslation(EndUserComponent.IDEA_MESSAGES_STATUS_FILTER);
  const cx = useClassNameMapper(localStyles);

  const { data: propertiesData, loading: propertiesLoading } = useQueryWithTracing<
    IdeaStatusInformationQuery,
    IdeaStatusInformationQueryVariables
  >(module, ideaStatusPropertiesQuery, {
    variables: {
      id: contextNode.id,
      useIdeaStatusProperties: true
    }
  });

  const isIdeaMessageStatusEnabled = (propertiesData?.coreNode as Idea)?.ideaStatusProperties
    ?.isIdeaStatusEnabled;

  const { data: ideaGroupsResult, loading: statusLoading } = useQueryWithTracing<
    IdeaStatusGroupsQuery,
    IdeaStatusGroupsQueryVariables
  >(module, ideaStatusGroupsQuery, {
    variables: {
      id: contextNode.id,
      useGroupKey: true,
      useDescription: false,
      useLabel: false
    },
    skip: !isIdeaMessageStatusEnabled
  });

  if (
    statusLoading ||
    !(ideaGroupsResult?.coreNode as Idea)?.ideaStatusGroups ||
    propertiesLoading ||
    !isIdeaMessageStatusEnabled ||
    i18n.loading
  ) {
    return null;
  }

  let ideaStatusList: StatusItem[] = [
    {
      statusKey: '',
      text: i18n.formatMessage('allIdeaStatuses', {
        suffix: statusGroup ? 'group' : 'all',
        groupTitle: statusGroup?.groupTitle
      })
    }
  ];
  for (const group of (ideaGroupsResult.coreNode as Idea).ideaStatusGroups.groups) {
    if (statusGroup && group.groupKey === statusGroup.groupKey) {
      ideaStatusList = [...ideaStatusList, ...group.statuses];
    } else if (!statusGroup) {
      ideaStatusList = [...ideaStatusList, ...group.statuses];
    }
  }

  const toggleStatus = (item: StatusItem) => {
    let newSelection: StatusItem[];
    const alreadySelected = selectedStatuses.includes(item.statusKey);

    if (item.statusKey === '') {
      newSelection = [item];
    } else {
      const filtered = selectedStatuses.filter(statusKey => statusKey !== '');
      if (alreadySelected) {
        newSelection = filtered
          .filter(statusKey => statusKey !== item.statusKey)
          .map(statusKey => {
            const text =
              ideaStatusList.find(status => status.statusKey === statusKey)?.text ?? statusKey;
            return { statusKey, text };
          });
      } else {
        newSelection = [
          ...filtered.map(statusKey => {
            const text =
              ideaStatusList.find(status => status.statusKey === statusKey)?.text ?? statusKey;
            return { statusKey, text };
          }),
          item
        ];
      }
    }
    onChange(newSelection);
  };

  /**
   * Function to get the label for the dropdown toggle.
   *
   * @returns {string} The label for the dropdown toggle.
   */
  function getDropdownLabel(): string {
    if (selectedStatuses.length === 0 || selectedStatuses.includes('')) {
      return i18n.formatMessage('allIdeaStatuses', {
        suffix: statusGroup ? 'group' : 'all',
        groupTitle: statusGroup?.groupTitle
      });
    }
    const firstStatus =
      ideaStatusList.find(status => status.statusKey === selectedStatuses[0])?.text ?? '';
    const othersCount = selectedStatuses.length - 1;
    return othersCount > 0
      ? i18n.formatMessage('dropdownLabel', { status: firstStatus, othersCount: othersCount })
      : firstStatus;
  }

  return (
    <Dropdown
      className={cx(className, 'lia-g-py-5')}
      data-testid="IdeaMessagesStatusFilter.Dropdown"
      onToggle={isOpen => setDropdownOpen(isOpen)}
      show={dropdownOpen}
    >
      <Dropdown.Toggle
        as={Button}
        variant={ButtonVariant.LINK}
        size="sm"
        className={cx('lia-g-dropdown-toggle lia-sort-dropdown')}
        aria-label={getDropdownLabel()}
      >
        {getDropdownLabel()}
      </Dropdown.Toggle>

      <Dropdown.Menu align="right" popperConfig={dropdownPopperConfig} renderOnMount>
        {ideaStatusList
          .filter(item => item.statusKey !== '')
          .map((item, index) => (
            <IdeaMessagesStatusFilterDropdownItem
              key={`${item.statusKey}-${index}`}
              item={item}
              contextNode={contextNode}
              selectedStatuses={selectedStatuses}
              onToggle={toggleStatus}
            />
          ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default IdeaMessagesStatusFilterDropdown;
